/**
 * Template Approval Panel Component
 *
 * FERP CRITICAL: Life-critical dual-control approval UI for workflow templates
 * Domain-agnostic approval workflow visualization and interaction
 *
 * Principles:
 * - All data from backend (ApprovalInstanceRepository)
 * - Dual-control visualization for HIPAA templates
 * - Complete audit trail display
 * - Repository pattern (no direct API calls)
 * - Mantine UI components only
 * - 16 primitive types (ApprovalPolicy, ApprovalInstance)
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Card,
  Stack,
  Group,
  Text,
  Badge,
  Button,
  Textarea,
  Divider,
  Timeline,
  Paper,
  Title,
  Alert,
  List,
  ThemeIcon,
  Tooltip,
  ActionIcon,
  Accordion,
  Checkbox
} from '@mantine/core';
import {
  IconShieldCheck,
  IconShieldX,
  IconAlertTriangle,
  IconClock,
  IconUserCheck,
  IconFileCheck,
  IconAlertCircle,
  IconInfoCircle,
  IconChecklist,
  IconCertificate
} from '@tabler/icons-react';
import {
  useTemplateApproval,
  useDualControlApproval,
  DecisionType,
  type ApprovalInstance,
  type ApprovalEvidence
} from '../hooks/useTemplateApproval';
import { useAuth } from '../security/useAuth';

interface TemplateApprovalPanelProps {
  templateId: string;
  templateVersion: string;
  instanceId?: string;
  mode?: 'submit' | 'review' | 'status';
  onApprovalComplete?: (instance: ApprovalInstance) => void;
  onApprovalRejected?: (instance: ApprovalInstance) => void;
}

/**
 * Template Approval Panel
 *
 * Displays approval workflow status and allows recording approval decisions.
 * Implements dual-control approval for life-critical templates (HIPAA compliance).
 */
export function TemplateApprovalPanel({
  templateId,
  templateVersion,
  instanceId,
  mode = 'status',
  onApprovalComplete,
  onApprovalRejected
}: TemplateApprovalPanelProps) {
  const { user } = useAuth();
  const {
    currentApproval,
    isLoading,
    error,
    submitForApproval,
    recordDecision,
    getApprovalStatus
  } = useTemplateApproval();

  const {
    isDualControlRequired,
    isDualControlSatisfied,
    canPublish,
    isRejected,
    isPending,
    approvalCount,
    rejectionCount
  } = useDualControlApproval(currentApproval);

  // Local state for decision recording (FERP: DecisionType enum from backend)
  const [decisionType, setDecisionType] = useState<DecisionType>(DecisionType.APPROVED);
  const [comments, setComments] = useState('');
  const [evidenceChecklist, setEvidenceChecklist] = useState<string[]>([]);
  const [certifications, setCertifications] = useState<string[]>([]);
  const [complianceFrameworks, setComplianceFrameworks] = useState<string[]>([]);

  // Load approval status on mount
  useEffect(() => {
    if (instanceId) {
      getApprovalStatus(instanceId);
    }
  }, [instanceId, getApprovalStatus]);

  // Handle submit for approval
  const handleSubmitForApproval = async () => {
    try {
      const instance = await submitForApproval(templateId, templateVersion);
      // Auto-load the new approval status (FERP: camelCase field names)
      if (instance.instanceId) {
        await getApprovalStatus(instance.instanceId);
      }
    } catch (err) {
      console.error('Failed to submit for approval:', err);
    }
  };

  // Handle record decision
  const handleRecordDecision = async () => {
    if (!currentApproval) return;

    try {
      // FERP: recordDecision now takes DecisionType, comments, revisionItems (no evidence param)
      await recordDecision(
        currentApproval.instanceId,
        decisionType,
        comments,
        [] // revisionItems - empty for now, future enhancement
      );

      // Reset form
      setComments('');
      setEvidenceChecklist([]);
      setCertifications([]);
      setComplianceFrameworks([]);

      // Trigger callbacks based on decision
      if (decisionType === 'APPROVED' && canPublish && onApprovalComplete) {
        onApprovalComplete(currentApproval);
      } else if (decisionType === 'REJECTED' && onApprovalRejected) {
        onApprovalRejected(currentApproval);
      }
    } catch (err) {
      console.error('Failed to record decision:', err);
    }
  };

  // Render criticality badge
  const getCriticalityColor = (criticality: string) => {
    switch (criticality) {
      case 'CRITICAL': return 'red';
      case 'HIGH': return 'orange';
      case 'MEDIUM': return 'yellow';
      case 'LOW': return 'blue';
      default: return 'gray';
    }
  };

  // Render status badge
  const getStatusBadge = () => {
    if (!currentApproval) return null;

    if (isRejected) {
      return <Badge color="red" leftSection={<IconShieldX size={16} />}>REJECTED</Badge>;
    }

    if (canPublish) {
      return <Badge color="green" leftSection={<IconShieldCheck size={16} />}>APPROVED</Badge>;
    }

    if (isPending) {
      return <Badge color="blue" leftSection={<IconClock size={16} />}>PENDING</Badge>;
    }

    return <Badge color="gray">UNKNOWN</Badge>;
  };

  // Render submit mode
  if (mode === 'submit') {
    return (
      <Card shadow="sm" padding="lg" radius="md" withBorder>
        <Stack gap="md">
          <Title order={3}>Submit Template for Approval</Title>

          <Text size="sm" c="dimmed">
            Template: <strong>{templateId}</strong> v{templateVersion}
          </Text>

          <Alert icon={<IconInfoCircle />} color="blue">
            This will submit the template to the approval workflow. Required approvers and
            criticality will be automatically determined based on template metadata.
          </Alert>

          <Button
            onClick={handleSubmitForApproval}
            loading={isLoading}
            leftSection={<IconFileCheck size={16} />}
          >
            Submit for Approval
          </Button>

          {error && (
            <Alert icon={<IconAlertCircle />} color="red" title="Submission Error">
              {error.message}
            </Alert>
          )}
        </Stack>
      </Card>
    );
  }

  // No approval instance loaded
  if (!currentApproval) {
    return (
      <Card shadow="sm" padding="lg" radius="md" withBorder>
        <Stack gap="md" ta="center">
          <IconClock size={48} color="gray" />
          <Text c="dimmed">No approval instance found</Text>
          {mode === 'review' && (
            <Button variant="light" onClick={handleSubmitForApproval}>
              Submit for Approval
            </Button>
          )}
        </Stack>
      </Card>
    );
  }

  // Render approval status and review interface
  return (
    <Card shadow="sm" padding="lg" radius="md" withBorder>
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between">
          <Title order={3}>Template Approval</Title>
          {getStatusBadge()}
        </Group>

        <Divider />

        {/* Template Info */}
        <Paper p="md" withBorder>
          <Stack gap="xs">
            <Group>
              <Text fw={500}>Template:</Text>
              <Text>{currentApproval.templateId} v{currentApproval.templateVersion}</Text>
            </Group>
            <Group>
              <Text fw={500}>Criticality:</Text>
              <Badge color={getCriticalityColor(currentApproval.criticality)}>
                {currentApproval.criticality}
              </Badge>
            </Group>
            <Group>
              <Text fw={500}>Dual Control:</Text>
              {isDualControlRequired ? (
                <Badge color="red" leftSection={<IconShieldCheck size={14} />}>
                  REQUIRED (2 approvers)
                </Badge>
              ) : (
                <Badge color="blue">NOT REQUIRED</Badge>
              )}
            </Group>
            <Group>
              <Text fw={500}>Deadline:</Text>
              <Text>{new Date(currentApproval.deadline).toLocaleString()}</Text>
            </Group>
          </Stack>
        </Paper>

        {/* Dual Control Status */}
        {isDualControlRequired && (
          <Alert
            icon={isDualControlSatisfied ? <IconShieldCheck /> : <IconAlertTriangle />}
            color={isDualControlSatisfied ? 'green' : 'orange'}
            title="Dual Control Status"
          >
            {approvalCount} of 2 required approvals received
            {isDualControlSatisfied && ' ✓ Can publish to production'}
          </Alert>
        )}

        {/* Required Approvers */}
        <Accordion variant="contained">
          <Accordion.Item value="required-approvers">
            <Accordion.Control icon={<IconUserCheck size={20} />}>
              Required Approvers ({currentApproval.requiredApprovers.length})
            </Accordion.Control>
            <Accordion.Panel>
              <List spacing="xs">
                {currentApproval.requiredApprovers.map((approver, idx) => (
                  <List.Item
                    key={idx}
                    icon={
                      <ThemeIcon color="blue" size={24} radius="xl">
                        <IconUserCheck size={16} />
                      </ThemeIcon>
                    }
                  >
                    <Text fw={500}>{approver.role}</Text>
                    {approver.certifications && approver.certifications.length > 0 && (
                      <Text size="sm" c="dimmed">
                        Certifications: {approver.certifications.join(', ')}
                      </Text>
                    )}
                  </List.Item>
                ))}
              </List>
            </Accordion.Panel>
          </Accordion.Item>
        </Accordion>

        {/* Decision Timeline */}
        <Box>
          <Text fw={500} mb="sm">Approval Timeline</Text>
          <Timeline active={currentApproval.decisions.length} lineWidth={2}>
            {currentApproval.decisions.map((decision, idx) => (
              <Timeline.Item
                key={decision.decisionId}
                bullet={
                  decision.decisionType === DecisionType.APPROVED ? (
                    <IconShieldCheck size={16} />
                  ) : decision.decisionType === DecisionType.REJECTED ? (
                    <IconShieldX size={16} />
                  ) : (
                    <IconAlertTriangle size={16} />
                  )
                }
                title={`${decision.decisionType} by ${decision.approverName}`}
                color={
                  decision.decisionType === DecisionType.APPROVED
                    ? 'green'
                    : decision.decisionType === DecisionType.REJECTED
                    ? 'red'
                    : 'orange'
                }
              >
                <Text c="dimmed" size="sm">
                  {new Date(decision.decidedAt).toLocaleString()}
                </Text>
                {decision.comments && (
                  <Text size="sm" mt="xs">
                    {decision.comments}
                  </Text>
                )}
                {decision.revisionItems && decision.revisionItems.length > 0 && (
                  <Paper p="xs" mt="xs" withBorder>
                    <Text size="xs" fw={500}>Revision Items:</Text>
                    {decision.revisionItems.map((item, i) => (
                      <Text key={i} size="xs">
                        ✓ {item.fieldId}: {item.issue}
                        {item.suggestedFix && ` (Suggested: ${item.suggestedFix})`}
                      </Text>
                    ))}
                  </Paper>
                )}
              </Timeline.Item>
            ))}
          </Timeline>
        </Box>

        {/* Review Interface (for reviewers) */}
        {mode === 'review' && isPending && (
          <>
            <Divider label="Record Your Decision" labelPosition="center" />

            <Group grow>
              <Button
                variant={decisionType === DecisionType.APPROVED ? 'filled' : 'light'}
                color="green"
                onClick={() => setDecisionType(DecisionType.APPROVED)}
                leftSection={<IconShieldCheck size={16} />}
              >
                Approve
              </Button>
              <Button
                variant={decisionType === DecisionType.APPROVED_WITH_SUGGESTIONS ? 'filled' : 'light'}
                color="orange"
                onClick={() => setDecisionType(DecisionType.APPROVED_WITH_SUGGESTIONS)}
                leftSection={<IconAlertTriangle size={16} />}
              >
                Approve with Suggestions
              </Button>
              <Button
                variant={decisionType === DecisionType.REJECTED ? 'filled' : 'light'}
                color="red"
                onClick={() => setDecisionType(DecisionType.REJECTED)}
                leftSection={<IconShieldX size={16} />}
              >
                Reject
              </Button>
            </Group>

            <Textarea
              label="Comments"
              placeholder="Provide detailed reasoning for your decision..."
              value={comments}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setComments(e.currentTarget.value)}
              minRows={3}
              required
            />

            <Box>
              <Text fw={500} size="sm" mb="xs">Evidence (optional but recommended)</Text>
              <Accordion variant="separated">
                <Accordion.Item value="checklist">
                  <Accordion.Control icon={<IconChecklist size={20} />}>
                    Checklist Items Verified
                  </Accordion.Control>
                  <Accordion.Panel>
                    <Checkbox.Group value={evidenceChecklist} onChange={setEvidenceChecklist}>
                      <Stack gap="xs">
                        <Checkbox value="metadata_complete" label="Metadata completeness verified" />
                        <Checkbox value="primitives_valid" label="All primitives validated" />
                        <Checkbox value="compliance_checked" label="Compliance requirements checked" />
                        <Checkbox value="security_reviewed" label="Security review completed" />
                      </Stack>
                    </Checkbox.Group>
                  </Accordion.Panel>
                </Accordion.Item>

                <Accordion.Item value="certifications">
                  <Accordion.Control icon={<IconCertificate size={20} />}>
                    Certifications Validated
                  </Accordion.Control>
                  <Accordion.Panel>
                    <Checkbox.Group value={certifications} onChange={setCertifications}>
                      <Stack gap="xs">
                        <Checkbox value="hipaa" label="HIPAA Compliance" />
                        <Checkbox value="sox" label="SOX Compliance" />
                        <Checkbox value="pci" label="PCI DSS Compliance" />
                      </Stack>
                    </Checkbox.Group>
                  </Accordion.Panel>
                </Accordion.Item>
              </Accordion>
            </Box>

            <Button
              onClick={handleRecordDecision}
              loading={isLoading}
              disabled={!comments.trim()}
              size="lg"
              leftSection={<IconFileCheck size={20} />}
            >
              Submit Decision
            </Button>
          </>
        )}

        {/* Publish Status */}
        {canPublish && (
          <Alert icon={<IconShieldCheck />} color="green" title="Ready to Publish">
            This template has received all required approvals and can be published to production.
          </Alert>
        )}

        {isRejected && (
          <Alert icon={<IconShieldX />} color="red" title="Approval Rejected">
            This template has been rejected and cannot be published.
          </Alert>
        )}

        {error && (
          <Alert icon={<IconAlertCircle />} color="red" title="Error">
            {error.message}
          </Alert>
        )}
      </Stack>
    </Card>
  );
}

/**
 * Pending Approvals List Component
 *
 * Displays list of all pending approvals for current user
 */
interface PendingApprovalsListProps {
  onSelectApproval?: (instance: ApprovalInstance) => void;
}

export function PendingApprovalsList({ onSelectApproval }: PendingApprovalsListProps) {
  const { pendingApprovals, isLoading, listPendingApprovals } = useTemplateApproval();

  useEffect(() => {
    listPendingApprovals();
  }, [listPendingApprovals]);

  if (isLoading) {
    return <Text c="dimmed">Loading pending approvals...</Text>;
  }

  if (pendingApprovals.length === 0) {
    return (
      <Alert icon={<IconInfoCircle />} color="blue">
        No pending approvals
      </Alert>
    );
  }

  return (
    <Stack gap="md">
      {pendingApprovals.map((approval) => (
        <Card
          key={approval.instanceId}
          shadow="sm"
          padding="md"
          radius="md"
          withBorder
          style={{ cursor: onSelectApproval ? 'pointer' : 'default' }}
          onClick={() => onSelectApproval?.(approval)}
        >
          <Group justify="space-between">
            <Box>
              <Text fw={500}>{approval.templateId} v{approval.templateVersion}</Text>
              <Text size="sm" c="dimmed">
                {approval.dualControlRequired ? '🔒 Dual Control Required' : 'Single Approval'}
              </Text>
            </Box>
            <Badge color={approval.criticality === 'CRITICAL' ? 'red' : 'blue'}>
              {approval.criticality}
            </Badge>
          </Group>
          <Text size="xs" c="dimmed" mt="xs">
            Deadline: {new Date(approval.deadline).toLocaleString()}
          </Text>
        </Card>
      ))}
    </Stack>
  );
}
